import find from 'lodash/find';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import { acceptHMRUpdate, defineStore } from 'pinia';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { STARTER_2024_PACKAGE } from '@/free/free.constants.js';
import { getDaysRemainingInTrial } from '@/free/free.utils.js';
import { checkStagingServer } from '@/settings/environments/environment-settings.utils';
import { FY25_MODEL_STRING, NOVUS_PRICING_MODEL } from '@/settings/subscription-settings/utils';
import { useAppsStore } from '@/stores/apps.store';
import { retrieveMetadataByField } from '@/stores/metadata.utils';
import { useProductLinesStore, type ProductLine } from '@/stores/product-lines.store';
import {
    getFeatureFlag,
    getTrialEndDate,
    subIsDefaultPlan,
    subIsInTrial,
} from '@/stores/subscription.helpers.js';
import {
    checkScimEnabledForSubscription,
    deleteSetting,
    extendFreeTrial,
    fetchEventCount,
    fetchSetting,
    fetchSubscription,
    getOIDCConfiguration,
    getSubscriptionPromptCount,
    getSubscriptionVisitorMau,
    recordRaw,
    startFreeTrial,
    SUB_PROPERTY_API_BINDING_KEYS,
    update,
    updateAutoGrantAccessExpiresOn,
    updateAutoProvFlag,
    updateByName,
    updateEmailThrottleSettings,
    updateFlag,
    updateInsightsFlag,
    updateJourneyThrottleSettings,
    updateOIDCConfiguration,
    updatePlan,
    updateSemanticSearchFlag,
    updateSetting,
    updateSubscriptionExtensionAttributes,
} from '@/stores/subscription.utils.js';
import i18nDateTimeFormatter from '@/utils/i18n/i18n-datetime-formatter';
import { setCurrentTimezoneForAppWideMoment } from '@/utils/moment-wrapper';

function refreshSubscriptionLocale (): void {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { refreshLocale }: typeof import('@/utils/i18n/i18n.js') = require('@/utils/i18n/i18n.js');
    refreshLocale();
}

function applySubscriptionFlags (featureFlags: Subscription['featureFlags']): void {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { setSubscriptionFlags }: typeof import('@/data/flags/local-flags') = require('@/data/flags/local-flags');
    setSubscriptionFlags(featureFlags as unknown as string[]);
}

export interface Subscription {
    id?: string;
    displayName?: string;
    timeZone?: string;
    locale?: string;
    pricingModel?: string;
    trialEndDate?: number;
    eventCount?: number;
    identifyStartingEventTime?: number;
    isScimEnabled?: boolean;
    setupKey?: string;
    mauHashCutoff?: number;
    accountIdSortStart?: number;
    firstEventTime?: number;
    eventFileShards?: number;
    eventRate?: number;
    maxSessionInactivity?: number;
    featureFlags?: Record<string, boolean>;
    productLines?: ProductLine[];
    applications?: unknown[];
    extensionAttributes?: Record<string, unknown>;
    emailSettings?: {
        throttlesettings?: ThrottleSettings;
        emailIdentities?: Record<string, unknown>;
        [key: string]: unknown;
    };
    journeySettings?: { throttlesettings?: ThrottleSettings; [key: string]: unknown };
    trainingAttributes?: Record<string, unknown>;
    [key: string]: unknown;
}

interface ThrottleSettings {
    enabled?: boolean;
    [key: string]: unknown;
}

interface SettingValue {
    value: unknown;
    label?: unknown;
}

interface OIDCConfiguration {
    clientId?: string;
    trustedIssuer?: string;
    [key: string]: unknown;
}

interface SubscriptionState {
    active: Subscription;
    activeSubMauCount: number;
    activeSubPromptCount: number;
    allSubscriptionsMap: Record<string, Subscription>;
    currentMauLimit: number;
    error: unknown;
    fetching: boolean;
    hasStagingServers: boolean;
    map: Record<string, Subscription>;
    novusSubscription: Subscription | null;
    oidc: OIDCConfiguration;
    settings: Record<string, SettingValue | null>;
    updatingFlag: string | null;
    updatingProperty: string | null;
    vendorId: string | null;
}

interface LegacyAuthStore {
    getters: {
        'auth/hasOnlyNovusSub': boolean;
        'auth/hasOnlyTrainingSub': boolean;
        'auth/isAuthenticated': boolean;
        'auth/isOps': boolean;
        'auth/isPendozer': boolean;
    };
    dispatch: (type: string, payload?: unknown) => Promise<unknown>;
}

type LoadSettingOptions = {
    setting?: string;
    reFetch?: boolean;
    retrieveMetadata?: boolean;
    abortSignal?: AbortSignal;
};

const loadSettingPromises: Record<string, Promise<void> | null> = {};

function getLegacyAuthStore (): LegacyAuthStore {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('@/vuex/store').default as LegacyAuthStore;
}

export function __resetLoadSettingPromisesForUnitTests (): void {
    for (const key of Object.keys(loadSettingPromises)) {
        loadSettingPromises[key] = null;
    }
}

export function getInitialState (): SubscriptionState {
    return {
        active: {},
        activeSubMauCount: -1,
        activeSubPromptCount: -1,
        allSubscriptionsMap: {},
        currentMauLimit: -1,
        error: null,
        fetching: false,
        hasStagingServers: false,
        map: {},
        novusSubscription: null,
        oidc: {},
        settings: {},
        updatingFlag: null,
        updatingProperty: null,
        vendorId: null,
    };
}

export const useSubscriptionStore = defineStore('subscription', {
    state: getInitialState,
    getters: {
        allSubscriptions: (state): Subscription[] => Object.values(state.allSubscriptionsMap),
        defaultSubscription (): Subscription | undefined {
            return find(this.map, (subscription) => subscription.id !== get(this.sampleDataSubscription, 'id'));
        },
        daysRemainingInTrial (): number {
            return getDaysRemainingInTrial(this.active?.trialEndDate || 0, this.isInAutoProvisionedTrial);
        },
        hadUpfrontTrial (): boolean {
            return this.getFeatureFlag('hadUpfrontTrial');
        },
        isEligibleForTrial (): boolean {
            return this.active?.trialEndDate === 0 && this.isPendoFree;
        },
        isEligibleForPreInstallTrial (): boolean {
            return this.active?.trialEndDate === 0 && !this.snippetInstalled && this.isPendoFree;
        },
        canStartTrial (): boolean {
            return this.isEligibleForTrial && this.isPendoFree;
        },
        canUpgrade (): boolean {
            return (
                this.isPendoFree &&
                this.purchasableSelfServicePlan === STARTER_2024_PACKAGE.planId &&
                hasSegmentFlag('AccountHqInIndia')
            );
        },
        isFreeAndNotIndia (): boolean {
            return (
                this.isPendoFree &&
                !(
                    this.purchasableSelfServicePlan === STARTER_2024_PACKAGE.planId &&
                    hasSegmentFlag('AccountHqInIndia')
                )
            );
        },
        purchasableSelfServicePlan (): unknown {
            return this.active?.purchasableSelfServicePlan;
        },
        subscriptionPackageTiers (): Record<string, unknown> {
            return {
                isPendoFree: this.isPendoFree,
                isInTrial: this.isInTrial,
                isStarterPackage: this.isStarterPackage,
                isStarter2024Package: this.isStarter2024Package,
                isEligibleForTrial: this.isEligibleForTrial,
                canStartTrial: this.canStartTrial,
                daysRemainingInTrial: this.daysRemainingInTrial,
                canUpgrade: this.canUpgrade,
                purchasableSelfServicePlan: this.purchasableSelfServicePlan,
            };
        },
        featureFlagList: (state): string[] | undefined => {
            const { featureFlags } = state.active;
            if (!featureFlags) return;

            return Object.keys(featureFlags).filter((flag) => featureFlags[flag]);
        },
        trialHistory: (state): unknown => state.active?.trialHistory,
        isInTrial: (state): boolean => subIsInTrial(state.active),
        isInAutoProvisionedTrial (): boolean {
            return this.getFeatureFlag('autoProvisioned') && this.active.trialEndDate !== 0;
        },
        isAutoProvisionedTrialExpired (): boolean {
            return this.isInAutoProvisionedTrial && Number(this.active.trialEndDate) < Date.now();
        },
        isStarterPackage (): boolean {
            return this.getFeatureFlag('starterPackage') && !this.isPendoFree;
        },
        isStarter2024Package (): boolean {
            return this.getFeatureFlag('starter2024Package') && !this.isPendoFree;
        },
        isFreeOrStarter (): boolean {
            return this.isPendoFree || this.isStarterPackage || this.isStarter2024Package;
        },
        isDefaultPlan: (state): boolean => subIsDefaultPlan(state.active),
        isPartnerAnalytics (): boolean {
            return this.getFeatureFlag('partnerAnalytics');
        },
        trialEndDate: (state): number => getTrialEndDate(state.active),
        getActiveSub: (state): Subscription => state.active,
        getFeatureFlag: (state) => (flag: string): boolean => getFeatureFlag(state.active, flag),
        getSubFeatureFlag: () => (subscription: Subscription, flag: string): boolean =>
            !!get(subscription, `featureFlags.${flag}`, false),
        isAdoptOnCore (): boolean {
            const productLine = useProductLinesStore().productLineByType('adoptEndUser');

            return !!(productLine?.purchased && get(productLine, 'metadata.userInterface') === 'engage');
        },
        isAdoptEndUser: (): boolean => useProductLinesStore().hasProductLinePurchased('adoptEndUser'),
        isTrainingSubscription (): boolean {
            return this.isAdoptEndUser;
        },
        isAdoptReseller: (): boolean => useProductLinesStore().hasProductLinePurchased('adoptReseller'),
        isActiveTrainingPartner (): boolean {
            return this.isAdoptReseller;
        },
        isActiveViaPartner (): boolean {
            return this.isAdoptReseller;
        },
        isDigitalAdoptionOnly: (): boolean => {
            const productLine = useProductLinesStore().productLineByType('adopt');

            return !!(productLine?.purchased && get(productLine, 'metadata.userInterface') === 'adopt');
        },
        hasVisualDesignStudioEnabled: (): boolean => {
            const productLine = useProductLinesStore().productLineByType('oneUIEngineeringUse');

            return !!(productLine?.purchased && get(productLine, 'metadata.visualDesignStudioEnabled'));
        },
        hasExtensionEnabled: (): boolean => {
            const productLine = useProductLinesStore().productLineByType('oneUIEngineeringUse');

            return !!(productLine?.purchased && get(productLine, 'metadata.extensionApplicationEnabled'));
        },
        isPendoFree (): boolean {
            return this.getFeatureFlag('pendoFree') && !this.isInTrial;
        },
        isMemberOfSubscription: (state) => (id: string): boolean => !!state.map[id],
        list: (state): Subscription[] => Object.values(state.map),
        mauCount: (state): number => state.activeSubMauCount,
        mauLimit: (state): number => state.currentMauLimit,
        loadingMauData: (state): boolean => state.currentMauLimit === -1 || state.activeSubMauCount === -1,
        isOverMauLimit (): boolean {
            if (!this.getFeatureFlag('enforceMauLimiting') || this.isInTrial || this.currentMauLimit === -1) {
                return false;
            }

            return this.activeSubMauCount > this.currentMauLimit;
        },
        promptCount: (state): number => state.activeSubPromptCount,
        promptLimit: (): number => {
            const productLine = useProductLinesStore().productLineByType('agentAnalytics');
            const nestedLimit = get(productLine, 'metadata.agentAnalyticsProductLineMetadata.monthlyPromptLimit');
            const flatLimit = get(productLine, 'metadata.monthlyPromptLimit');

            return Number(nestedLimit ?? flatLimit ?? -1);
        },
        hasAgentAnalyticsPurchased: (): boolean =>
            useProductLinesStore().hasProductLinePurchased('agentAnalytics'),
        isAgentAnalyticsUnlimited: (): boolean => {
            const productLine = useProductLinesStore().productLineByType('agentAnalytics');
            const nestedEndDate = get(productLine, 'metadata.agentAnalyticsProductLineMetadata.unlimitedEndDate');
            const flatEndDate = get(productLine, 'metadata.unlimitedEndDate');
            const endDate = nestedEndDate ?? flatEndDate;
            if (!endDate) return false;

            return new Date().toISOString().slice(0, 10) <= String(endDate);
        },
        isOverPromptLimit (): boolean {
            if (this.isInTrial || this.promptLimit === -1 || this.isAgentAnalyticsUnlimited) return false;

            return this.activeSubPromptCount >= this.promptLimit;
        },
        isFreeAndOverMauLimit (): boolean {
            return this.isPendoFree && this.isOverMauLimit;
        },
        requireSSO (): boolean {
            return this.getFeatureFlag('requireGoogleSSO') ||
                this.getFeatureFlag('requireSAML') ||
                this.getFeatureFlag('requireFeedbackSAML');
        },
        sampleDataSubscription: (state): Subscription | null =>
            Object.values(state.map).find((subscription) =>
                get(subscription, 'featureFlags.sampleDataSubscription'),
            ) || null,
        eventRate: (state): number => Number(get(state, 'active.eventRate', 1)),
        isUpdatingAutoGrantAccessExpiresOn: (state): boolean =>
            state.updatingProperty === 'autoGrantAccessExpiresOn',
        hasAIGeneratedThemes (): boolean {
            return this.getFeatureFlag('enableNPSThemesAssignment') &&
                this.getFeatureFlag('enableNPSThemesTriage');
        },
        hasNPSLLM (): boolean {
            return this.getFeatureFlag('enableNPSLLM');
        },
        hasInsights (): boolean {
            return this.getFeatureFlag('enableMlChurnInsights') && this.getFeatureFlag('enableMlAnalysis');
        },
        snippetInstalled: (state): boolean =>
            Number(state.active.eventCount) > 0 ||
            useAppsStore().mobileList.some((app) => app.integrated) ||
            useAppsStore().extensionList.some((app) => app.integrated),
        setupKey: (state): string | undefined => state.active.setupKey,
        hasSessionReplayEnabled: (): boolean =>
            useProductLinesStore().hasProductLinePurchased('sessionReplay'),
        appSettingsFeatureAvailability (): Record<string, boolean> {
            const productLines = useProductLinesStore();

            return {
                hasLocalization:
                    productLines.hasProductLinePurchased('guides', 'localizationEnabled') ||
                    productLines.hasProductLinePurchased('nps') ||
                    productLines.hasProductLinePurchased('voc', 'ideas'),
                hasTaggingAndGuide: this.getFeatureFlag('enableP2Access'),
                hasSessionReplay: this.hasSessionReplayEnabled,
            };
        },
        hasExtensionAppCodeBlocksEnabled: (): boolean =>
            useProductLinesStore().hasProductLinePurchased('guides', 'extensionAppCustomCodeBlocksEnabled'),
        mauHashCutoff: (state): number | undefined => state.active.mauHashCutoff,
        accountIdSortStart: (state): number | null => state.active.accountIdSortStart || null,
        hasAccountLevelAnalyticsEnabled (): boolean {
            return this.getFeatureFlag('enableAccounts');
        },
        hasIdentityManagementEnabled: (state): boolean =>
            !!get(state.active, 'identifyStartingEventTime', false),
        isFY25PricingModel: (state): boolean => state.active?.pricingModel === FY25_MODEL_STRING,
        isScimEnabled: (state): boolean => get(state.active, 'isScimEnabled', false),
        subLocale: (state): string | undefined => state.active.locale,
        hasTaggingScreenshotsEnabled (): boolean {
            return !this.getFeatureFlag('disableScreenshots');
        },
        trainingAttributes: (state): Record<string, unknown> =>
            get(state.active, 'trainingAttributes', {}) as Record<string, unknown>,
        minVisitorsInSegment: (state): number => Number(get(state.active, 'minVisitorsInSegment', 0)),
        getSubscriptionEmailThrottleSettings: (state): ThrottleSettings =>
            state.active.emailSettings?.throttlesettings || {},
        getSubscriptionJourneyThrottleSettings: (state): ThrottleSettings =>
            state.active.journeySettings?.throttlesettings || {},
    },
    actions: {
        setActive ({ subscription }: { subscription: Subscription }) {
            this.active = subscription;
            if (subscription.timeZone) {
                i18nDateTimeFormatter.timeZone = subscription.timeZone;
                setCurrentTimezoneForAppWideMoment(subscription.timeZone);
            }
            refreshSubscriptionLocale();
            applySubscriptionFlags(subscription.featureFlags);
        },
        setActiveSubMauCount (mauCount: number) {
            this.activeSubMauCount = mauCount;
        },
        setCurrentMauLimit ({ newMauLimit }: { newMauLimit: number }) {
            this.currentMauLimit = newMauLimit;
        },
        setActiveSubPromptCount (promptCount: number) {
            this.activeSubPromptCount = promptCount;
        },
        setActiveById ({ id }: { id: string }) {
            this.setActive({ subscription: this.map[id] });
        },
        setMap ({ map }: { map: Record<string, Subscription> }) {
            this.map = map;
        },
        setNovusSubscription ({ subscription }: { subscription: Subscription | null }) {
            this.novusSubscription = subscription;
        },
        setSubById ({ subscription }: { subscription: Subscription }) {
            this.map[subscription.id!] = subscription;
        },
        setHasStagingServers ({ hasStagingServers }: { hasStagingServers: boolean }) {
            this.hasStagingServers = hasStagingServers;
        },
        setFetching ({ fetching }: { fetching: boolean }) {
            this.fetching = fetching;
        },
        setSetting ({ setting, value }: { setting: string; value: SettingValue | null }) {
            this.settings[setting] = value;
        },
        addSubToList ({ subscription }: { subscription: Subscription }) {
            this.map[subscription.id!] = subscription;
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        setFlag (
            { flag, enabled, subscription }: { flag: string; enabled: boolean; subscription?: Subscription },
        ) {
            const targetSubscription = subscription || this.active;
            targetSubscription.featureFlags![flag] = enabled;
            applySubscriptionFlags(targetSubscription.featureFlags);
        },
        setUpdatingFlag ({ flag }: { flag: string | null }) {
            this.updatingFlag = flag;
        },
        setProperty ({ property, value }: { property: string; value: unknown }) {
            this.active[property] = value;
            if (property === 'timeZone') {
                i18nDateTimeFormatter.timeZone = value as string;
                setCurrentTimezoneForAppWideMoment(value as string);
            }
        },
        setUpdatingProperty ({ property }: { property: string | null }) {
            this.updatingProperty = property;
        },
        setOIDC (oidc: OIDCConfiguration) {
            this.oidc = oidc;
        },
        reset () {
            this.$reset();
        },
        setProductLines ({ productLines }: { productLines: ProductLine[] }) {
            this.active.productLines = productLines;
        },
        setAllSubscriptions ({ subscriptions }: { subscriptions: Record<string, Subscription> }) {
            this.allSubscriptionsMap = subscriptions;
        },
        async hydrate (
            { subscriptions, activeSubscription }: { subscriptions: Subscription[]; activeSubscription: Subscription },
        ) {
            const allSubscriptions = subscriptions.reduce<Record<string, Subscription>>((result, subscription) => {
                const rest = { ...subscription };
                delete rest.applications;
                delete rest.productLines;
                result[subscription.id!] = rest;

                return result;
            }, {});
            this.setAllSubscriptions({ subscriptions: allSubscriptions });

            const authStore = getLegacyAuthStore();
            let hasTrainingSub = false;
            let hasNovusSub = false;
            const isPendozer = authStore.getters['auth/isPendozer'];
            const subscriptionMapById = subscriptions.reduce<Record<string, Subscription>>((result, subscription) => {
                const productLines = subscription.productLines || [];
                const rest = { ...subscription };
                delete rest.applications;
                delete rest.productLines;
                const isAeuNotOnCore = productLines.some((productLine) =>
                    productLine.type === 'adoptEndUser' &&
                    productLine.purchased &&
                    get(productLine, 'metadata.userInterface') === 'adopt',
                );
                if (isAeuNotOnCore) {
                    hasTrainingSub = true;

                    return result;
                }
                if (subscription.pricingModel === NOVUS_PRICING_MODEL && !isPendozer) {
                    hasNovusSub = true;

                    return result;
                }
                result[subscription.id!] = rest;

                return result;
            }, {});
            this.setMap({ map: subscriptionMapById });

            const activeSubIsAdoptUIAeu = activeSubscription.productLines!.some((productLine) =>
                productLine.type === 'adoptEndUser' &&
                productLine.purchased &&
                get(productLine, 'metadata.userInterface') === 'adopt',
            );
            const isOps = authStore.getters['auth/isOps'];
            if (!isOps && activeSubIsAdoptUIAeu && this.list.length) {
                window.location.href = `${window.location.origin}/s/${this.list[0].id}/home`;

                return;
            }

            const activeSubIsNovus = activeSubscription.pricingModel === NOVUS_PRICING_MODEL && !isPendozer;
            if (!isOps && activeSubIsNovus && this.list.length) {
                this.setNovusSubscription({ subscription: activeSubscription });
                authStore.dispatch('auth/updateOnlyNovusSub', { onlyNovusSub: true });

                return;
            }

            if (!this.list.length) {
                const onlyNovusSub = !hasTrainingSub && hasNovusSub;
                if (onlyNovusSub) this.setNovusSubscription({ subscription: activeSubscription });
                authStore.dispatch('auth/updateOnlyTrainingSub', { onlyTrainingSub: hasTrainingSub });
                authStore.dispatch('auth/updateOnlyNovusSub', { onlyNovusSub });

                return;
            }

            this.setNovusSubscription({ subscription: null });
            this.setActive({ subscription: activeSubscription });
            this.getMauCount();
            this.updateCurrentMauLimit();
            this.fetchIsScimEnabled();
            const agentAnalytics = activeSubscription.productLines?.find(
                (productLine) => productLine.type === 'agentAnalytics',
            );
            if (agentAnalytics?.purchased) this.getPromptCount();
        },
        async loadActiveSubscription () {
            const { eventCount, id, isScimEnabled } = this.active;
            try {
                const activeSubscription = await fetchSubscription(id) as Subscription;
                const subscription = Object.assign({}, this.map[String(id)], activeSubscription);
                subscription.identifyStartingEventTime = activeSubscription.identifyStartingEventTime;
                subscription.pricingModel = activeSubscription.pricingModel;
                subscription.isScimEnabled = isScimEnabled;
                if (subscription.eventCount === 0 && Number(eventCount) > 0) subscription.eventCount = eventCount;
                this.setActive({ subscription });
                this.setActiveSubMauCount(-1);
                this.getMauCount();
                const coreProductLine = subscription.productLines!.find(
                    (productLine) => productLine.type === 'core',
                );
                this.setCurrentMauLimit({
                    newMauLimit: (get(coreProductLine, 'metadata.mauLimit') || -1) as number,
                });
                const agentAnalytics = subscription.productLines?.find(
                    (productLine) => productLine.type === 'agentAnalytics',
                );
                if (agentAnalytics?.purchased) this.getPromptCount();
            } catch (error) {
                this.error = error;
            }
        },
        async getHasStagingServers () {
            this.hasStagingServers = await checkStagingServer();
        },
        async loadSetting (
            { setting, reFetch = false, retrieveMetadata = true, abortSignal }: LoadSettingOptions = {},
        ) {
            if (!setting || (this.settings[setting] && !reFetch)) return;
            const promiseKey = `${setting}:${retrieveMetadata}`;
            if (!reFetch && loadSettingPromises[promiseKey]) return loadSettingPromises[promiseKey];

            const promise = (async () => {
                this.error = null;
                this.fetching = true;
                try {
                    const value = await fetchSetting(setting, abortSignal);
                    if (value && retrieveMetadata) {
                        const [kind, group, field] = value.split('.');
                        const { data } = await retrieveMetadataByField(kind, group, field, abortSignal);
                        this.setSetting({ setting, value: { value, label: data.DisplayName } });
                    } else if (value) {
                        this.setSetting({ setting, value: { value } });
                    }
                } catch (error) {
                    this.error = error;
                    throw error;
                } finally {
                    this.fetching = false;
                }
            })();
            loadSettingPromises[promiseKey] = promise;
            try {
                await promise;
            } finally {
                if (loadSettingPromises[promiseKey] === promise) loadSettingPromises[promiseKey] = null;
            }
        },
        setSettingValue ({ setting, value }: { setting: string; value: unknown }) {
            this.setSetting({ setting, value: { value } });
        },
        async updateSetting ({ setting, value, label }: { setting: string; value: unknown; label?: unknown }) {
            if (this.settings[setting]?.value === value) return;
            this.error = null;
            this.fetching = true;
            try {
                await updateSetting(setting, { value });
                this.setSetting({ setting, value: { value, label } });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async deleteSetting ({ setting }: { setting: string }) {
            this.fetching = true;
            try {
                await deleteSetting(setting);
                this.setSetting({ setting, value: null });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.fetching = false;
            }
        },
        async updateFlag (
            { flag, enabled, optimistic = false }: { flag: string; enabled: boolean; optimistic?: boolean },
        ) {
            const originalValue = this.getFeatureFlag(flag);
            if (optimistic) this.setFlag({ flag, enabled });
            this.updatingFlag = flag;
            try {
                await updateFlag({ flag, enabled });
                await this.loadActiveSubscription();
                if (!optimistic) this.setFlag({ flag, enabled });
            } catch (error) {
                if (optimistic) this.setFlag({ flag, enabled: originalValue });
                this.error = error;
                throw error;
            } finally {
                this.updatingFlag = null;
            }
        },
        async updateAutoProvFlag ({ id, reason }: { id: string; reason: string }) {
            try {
                await updateAutoProvFlag({ subscriptionId: id, reason });
                await this.loadActiveSubscription();
                this.setFlag({ flag: 'autoProvisioned', enabled: false });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.updatingFlag = null;
            }
        },
        async updateProperty ({ property, value }: { property: string; value: unknown }) {
            this.updatingProperty = property;
            try {
                const bindingKey = (SUB_PROPERTY_API_BINDING_KEYS as Record<string, string>)[property];
                if (bindingKey) await updateByName(property, { [bindingKey]: value });
                else await update({ ...this.active, [property]: value });
                this.setProperty({ property, value });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.updatingProperty = null;
            }
        },
        async startRecordingRawEvents () {
            try {
                const { recordUntil } = await recordRaw();
                this.setProperty({ property: 'recordUntil', value: recordUntil });
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async updateEventCount () {
            try {
                this.setProperty({ property: 'eventCount', value: await fetchEventCount() });
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async startFreeTrial (
            { duration = 14, checklist, upfront }: { duration?: number; checklist?: unknown; upfront?: boolean } = {},
        ) {
            try {
                const startTrial = startFreeTrial as (options: {
                    duration?: number;
                    checklist?: unknown;
                    upfront?: boolean;
                }) => Promise<Subscription>;
                const subscription = await startTrial({ duration, checklist, upfront });
                this.setActive({ subscription });
                this.setSubById({ subscription });
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async getMauCount () {
            try {
                const featureFlagList = this.featureFlagList!;
                const mauCount = await getSubscriptionVisitorMau({
                    mauOnly: featureFlagList.includes('mauOnly'),
                    showAnonymous: featureFlagList.includes('showAnonymous'),
                    isFY25PricingModel: this.isFY25PricingModel,
                });
                this.activeSubMauCount = mauCount;
            } catch (error) {
                this.error = error;
            }
        },
        setPromptCount (promptCount: number) {
            this.activeSubPromptCount = promptCount;
        },
        async getPromptCount () {
            try {
                this.activeSubPromptCount = await getSubscriptionPromptCount();
            } catch (error) {
                this.error = error;
            }
        },
        async extendFreeTrial ({ numDays, subscriptionId }: { numDays: number; subscriptionId: string }) {
            try {
                this.setActive({ subscription: await extendFreeTrial(numDays, subscriptionId) as Subscription });
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async fetchOIDC () {
            try {
                this.oidc = await getOIDCConfiguration();
            } catch (error) {
                this.error = error;
                this.oidc = { clientId: '', trustedIssuer: '' };
            }
        },
        async updateOIDC ({ body }: { body: Record<string, unknown> }) {
            this.oidc = await updateOIDCConfiguration({ ...body });
        },
        async updateAutoGrantAccessExpiresOn ({ property, value }: { property: string; value: unknown }) {
            this.updatingProperty = property;
            try {
                await updateAutoGrantAccessExpiresOn({ property, value });
                await this.loadActiveSubscription();
                this.setProperty({ property, value });
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.updatingProperty = null;
            }
        },
        async updateCurrentMauLimit () {
            const auth = getLegacyAuthStore().getters;
            try {
                if (auth['auth/isAuthenticated'] && !auth['auth/hasOnlyTrainingSub'] && !auth['auth/hasOnlyNovusSub']) {
                    const subscription = await fetchSubscription(this.active.id) as Subscription;
                    const coreProductLine = subscription.productLines?.find(
                        (productLine) => productLine.type === 'core',
                    );
                    this.currentMauLimit = (get(coreProductLine, 'metadata.mauLimit') || -1) as number;
                }
            } catch (error) {
                this.error = error;
            }
        },
        updateProductLines ({ productLines }: { productLines: ProductLine[] }) {
            this.active.productLines = productLines;
        },
        async updatePlan (
            {
                plan,
                isUpgrade,
                sfdcAccountId,
                sfdcAccountName,
                downgradeMethod = 'immediate',
                query,
            }: {
                plan: unknown;
                isUpgrade: boolean;
                sfdcAccountId?: string;
                sfdcAccountName?: string;
                downgradeMethod?: string;
                query?: string;
            },
        ) {
            const planBody: Record<string, unknown> = { plan };
            if (isUpgrade) {
                if (!sfdcAccountId) throw new Error('Upgrading a plan requires a SFDC Account ID');
                planBody.sfdcAccountId = sfdcAccountId;
                if (sfdcAccountName) planBody.sfdcAccountName = sfdcAccountName;
            } else {
                planBody.downgradeMethod = downgradeMethod;
            }
            try {
                await updatePlan(planBody, query);
                await this.loadActiveSubscription();
                useAppsStore().loadAll({ reFetch: true });
                useProductLinesStore().setMap({ map: keyBy(this.active.productLines || [], 'type') });
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async updateExtensionAttributes ({ extensionAttributes }: { extensionAttributes: Record<string, unknown> }) {
            const nextAttributes = Object.assign({}, this.active.extensionAttributes, extensionAttributes);
            try {
                this.setProperty({
                    property: 'extensionAttributes',
                    value: await updateSubscriptionExtensionAttributes(nextAttributes),
                });
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async updateInsightsFlag ({ set }: { set: boolean }) {
            this.updatingFlag = 'enableMlChurnInsights';
            try {
                await updateInsightsFlag({ set });
                await this.loadActiveSubscription();
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.updatingFlag = null;
            }
        },
        async updateSemanticSearchFlag ({ set }: { set: boolean }) {
            this.updatingFlag = 'entityEmbedding';
            try {
                await updateSemanticSearchFlag({ set });
                await this.loadActiveSubscription();
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.updatingFlag = null;
            }
        },
        async fetchIsScimEnabled ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if ('isScimEnabled' in this.active && !reFetch) return;
            try {
                this.setProperty({ property: 'isScimEnabled', value: await checkScimEnabledForSubscription() });
            } catch (error) {
                this.error = error;
                throw error;
            }
        },
        async saveEmailThrottleSettings ({ settings }: { settings: unknown }) {
            const { data } = await updateEmailThrottleSettings(settings);
            this.setProperty({
                property: 'emailSettings',
                value: { ...this.active.emailSettings, throttlesettings: data },
            });

            return data;
        },
        async saveJourneyThrottleSettings ({ settings }: { settings: unknown }) {
            const { data } = await updateJourneyThrottleSettings(settings);
            this.setProperty({
                property: 'journeySettings',
                value: { ...this.active.journeySettings, throttlesettings: data },
            });

            return data;
        },
        async ensureSubscriptionEmailThrottleSettingsLoaded () {
            if (!get(this.active, 'emailSettings.throttlesettings')) await this.loadActiveSubscription();
        },
        async ensureSubscriptionJourneyThrottleSettingsLoaded () {
            if (!get(this.active, 'journeySettings.throttlesettings')) await this.loadActiveSubscription();
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useSubscriptionStore, module.hot));
}
